import type { Influencer, InfluencerFilters, PaginatedResponse } from '@/types'
import api from '../services/api'
import { logError } from '../utils/sentry'
import { createPersistedStore } from './baseStore'

export interface Platform {
  id: string
  name: string
}

export interface Industries {
  id: string
  name: string
}

export interface InfluencerDetails {
  id: number
  name: string
  industries: string[]
  profilePhotoUrl?: string
  phone?: string
  instagramLink?: string
  instagramUsername?: string
  youtubeLink?: string
  youtubeUsername?: string
  tiktokLink?: string
  tiktokUsername?: string
  description?: string
  city?: string
  address?: string
}

interface InfluencerState {
  influencers: Influencer[]
  page: number
  pageSize: number
  totalItems: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  filters: InfluencerFilters
  availablePlatforms: Platform[]
  availableIndustries: Industries[]
  error: string | null
  loading: boolean
  isInitialized: boolean
  [key: string]: unknown
}

interface InfluencerActions {
  setFilters: (newFilters: InfluencerFilters | ((currentFilters: InfluencerFilters) => InfluencerFilters)) => void
  resetInitialization: () => void
  setPage: (page: number) => void
  fetchInfluencers: () => Promise<Influencer[]>
  initialize: () => Promise<void>
  fetchInfluencerDetails: (id: string | number) => Promise<InfluencerDetails | null>
}

const initialState: InfluencerState = {
  influencers: [],
  page: 1,
  pageSize: 12,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  filters: {
    industries: [],
    platforms: [],
  },
  availablePlatforms: [],
  availableIndustries: [],
  error: null,
  loading: false,
  isInitialized: false,
}

const buildQueryString = (params: Record<string, unknown>): string => {
  return Object.entries(params)
    .filter(([_, value]) => {
      if (value === null || value === undefined || value === '') return false
      return !(Array.isArray(value) && value.length === 0)
    })
    .reduce((acc: string[], [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        return acc.concat(value.map((v) => `${key}=${encodeURIComponent(String(v))}`))
      }
      return acc.concat(`${key}=${encodeURIComponent(String(value))}`)
    }, [])
    .join('&')
}

type SetState<T> = (state: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void

type GetState<T> = () => T

const actions = (set: SetState<InfluencerState>, get: GetState<InfluencerState & InfluencerActions>): InfluencerActions => ({
  setFilters: (newFilters) => {
    const state = get()
    const updatedFilters = {
      ...state.filters,
      ...(typeof newFilters === 'function' ? newFilters(state.filters) : newFilters),
    }

    set((state: InfluencerState) => ({
      ...state,
      filters: updatedFilters,
      page: 1,
    }))

    if (
      state.isInitialized &&
      ((updatedFilters.industries && updatedFilters.industries.length > 0) || (updatedFilters.platforms ?? []).length > 0)
    ) {
      void get().fetchInfluencers()
    }
  },

  resetInitialization: () => {
    set((state: InfluencerState) => ({ ...state, isInitialized: false }))
  },

  setPage: (page) => {
    set((state: InfluencerState) => ({ ...state, page }))
    void get().fetchInfluencers()
  },

  fetchInfluencers: async () => {
    const state = get()
    if (state.loading) return []

    set({ loading: true, error: null })

    try {
      const queryParams = {
        p: state.page,
        ps: state.pageSize,
        Industry: state.filters.industries,
        Platform: state.filters.platforms,
      }

      const queryString = buildQueryString(queryParams)
      const response = await api.get<PaginatedResponse<Influencer>>(`/brands/influencers${queryString ? `?${queryString}` : ''}`)

      let responseData: PaginatedResponse<Influencer> = {
        items: [],
        page: state.page,
        pageSize: state.pageSize,
        totalItems: 0,
        hasNextPage: false,
        hasPreviousPage: state.page > 1,
      }

      if (
        response?.data &&
        typeof response.data === 'object' &&
        'data' in response.data &&
        response.data.data &&
        typeof response.data.data === 'object' &&
        'items' in response.data.data
      ) {
        responseData = response.data.data as PaginatedResponse<Influencer>
      } else if (response?.data && typeof response.data === 'object' && 'items' in response.data) {
        responseData = response.data as PaginatedResponse<Influencer>
      } else if (Array.isArray(response?.data)) {
        responseData = {
          items: response.data as Influencer[],
          page: state.page,
          pageSize: state.pageSize,
          totalItems: response.data.length,
          hasNextPage: false,
          hasPreviousPage: state.page > 1,
        }
      }

      const {
        items = [],
        page = state.page,
        pageSize = state.pageSize,
        totalItems = 0,
        hasNextPage = false,
        hasPreviousPage = state.page > 1,
      } = responseData

      set({
        influencers: items,
        page,
        pageSize,
        totalItems,
        hasNextPage,
        hasPreviousPage,
        loading: false,
      })

      return items
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed to fetch influencers'
      logError('Failed to fetch influencers:', { error })
      set({ loading: false, error: errorMessage })
      throw error
    }
  },

  initialize: async () => {
    const state = get()
    if (state.isInitialized) return

    get().resetInitialization()

    try {
      const [platformsRes, industriesRes] = await Promise.all([api.get('/platforms'), api.get('/industries')])

      let platformItems = []
      let industryItems = []

      if (platformsRes?.data?.items) {
        platformItems = platformsRes.data.items
      } else if (platformsRes?.data?.data?.items) {
        platformItems = platformsRes.data.data.items
      } else if (Array.isArray(platformsRes?.data)) {
        platformItems = platformsRes.data
      }

      if (industriesRes?.data?.items) {
        industryItems = industriesRes.data.items
      } else if (industriesRes?.data?.data?.items) {
        industryItems = industriesRes.data.data.items
      } else if (Array.isArray(industriesRes?.data)) {
        industryItems = industriesRes.data
      }

      const platforms = (platformItems || []).map((name: string) => ({
        id: name,
        name,
      }))

      const industries = (industryItems || []).map((name: string) => ({
        id: name,
        name,
      }))

      set((state: InfluencerState) => {
        return {
          ...state,
          availablePlatforms: platforms,
          availableIndustries: industries,
          isInitialized: true,
        }
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed to initialize store'
      logError('Failed to initialize store:', { error })
      set({ error: errorMessage })
    }
  },

  fetchInfluencerDetails: async (id) => {
    try {
      const response = await api.get(`/brands/influencers/${id}`)
      if (response?.data) {
        return response.data as InfluencerDetails
      }
      return null
    } catch (error) {
      logError('Failed to fetch influencer details:', { error })
      return null
    }
  },
})

const useInfluencerStore = createPersistedStore<InfluencerState, InfluencerActions>('influencer-store', initialState, actions)

export default useInfluencerStore
